import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  DeletePatientItemDocument,
  GetPatientItemsDocument,
  PatientItemDataFragment,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';

type PatientDeletePromptProps = {
  patientItem: PatientItemDataFragment;
  onDeleted?: (value: PatientItemDataFragment) => void;
  onClose?: () => void;
};

export function PatientDeletePrompt({
  patientItem,
  onDeleted,
  onClose,
}: PatientDeletePromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [deletePatientItem] = useMutation(DeletePatientItemDocument, {
    refetchQueries: [GetPatientItemsDocument],
  });

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await deletePatientItem({
        variables: { patientId: patientItem.id },
      });

      if (result.data?.patientDelete.boolean) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'patient.patient-delete.success',
          }),
        });

        onDeleted?.(patientItem);

        onClose?.();
      } else if (result.errors) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: result.errors.map(option => option.message).join(','),
        });
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    patientItem,
    deletePatientItem,
    showAlertMessage,
    onDeleted,
    onClose,
  ]);

  if (!patientItem) return null;

  return (
    <ModalPrompt
      testId="patient-delete"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="patient.patient-delete.content" />
      </p>
    </ModalPrompt>
  );
}
